import type { Feature } from 'geojson';
import type { GeoJSONSource, Popup } from 'maplibre-gl';
import * as maplibregl from 'maplibre-gl';

import { Level, Level_t } from '@wemap/geo';
import type { WemapMap } from '@wemap/map';


type FeatureWithLevel = {
    feature: Feature,
    level: Level_t
};


export default class MapManager {

    private featuresWithLevel: FeatureWithLevel[] = [];

    private clickPopup: Popup | null = null;
    private heatmapEnabled: boolean = false;
    private heatmapIntensity: number = 0.5;
    private unsubscribeLevelChange: (() => void) | null = null;

    constructor(private wemapMap: WemapMap) {
        this.initialize();
    }

    async addGeoJSON(geojson: GeoJSON.FeatureCollection) {
        this.featuresWithLevel = geojson.features.map(f => ({
            feature: f,
            level: this.parseLevelValue(f.properties?.level)
        }));
        this.updateFeatures();
    }

    private parseLevelValue(level: any): Level_t {
        if (level === undefined || level === null) return null;

        if (typeof level === 'string') {
            return Level.fromString(level);
        }

        if (typeof level === 'number') {
            return level;
        }

        return null;
    }

    async initialize() {

        const map = this.wemapMap.maplibre;

        this.wemapMap.addSource('geojson', {
            type: 'geojson',
            data: {
                type: 'FeatureCollection',
                features: []
            }
        });

        this.wemapMap.addLayer({
            id: 'markers',
            type: 'circle',
            source: 'geojson',
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
                'circle-color': ['coalesce', ['get', 'marker-color'], '#000000'],
                'circle-radius': [
                    'match',
                    ['coalesce', ['get', 'marker-size'], 'medium'],
                    'small', 3,
                    'medium', 5,
                    'large', 7,
                    5 // Default size for 'medium'
                ],
                'circle-opacity': ['to-number', ['coalesce', ['get', 'score'], 1]]
            }
        });

        this.wemapMap.addLayer({
            id: 'heatmap',
            type: 'heatmap',
            source: 'geojson',
            filter: ['==', ['geometry-type'], 'Point'],
            paint: {
                'heatmap-weight': [
                    'interpolate',
                    ['linear'],
                    ['to-number', ['coalesce', ['get', 'score'], 0.5]],
                    0, 0,
                    1, 1
                ],
                'heatmap-intensity': 1,
                'heatmap-color': [
                    'interpolate',
                    ['linear'],
                    ['heatmap-density'],
                    0, 'rgba(33,102,172,0)',
                    0.2, 'rgb(103,169,207)',
                    0.4, 'rgb(209,229,240)',
                    0.6, 'rgb(253,219,199)',
                    0.8, 'rgb(239,138,98)',
                    1, 'rgb(178,24,43)'
                ],
                'heatmap-radius': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    0, 2,
                    9, 20
                ],
                'heatmap-opacity': 0.6
            }
        });

        // Initially hide heatmap layer
        map.setLayoutProperty('heatmap', 'visibility', 'none');
        map.setPaintProperty('heatmap', 'heatmap-intensity', this.heatmapIntensity);

        this.wemapMap.addLayer({
            id: 'lines',
            type: 'line',
            source: 'geojson',
            filter: ['==', ['geometry-type'], 'LineString'],
            paint: {
                'line-color': ['coalesce', ['get', 'stroke'], '#555555'],
                'line-width': ['to-number', ['coalesce', ['get', 'stroke-width'], 2]],
                'line-opacity': ['to-number', ['coalesce', ['get', 'stroke-opacity'], 1]]
            }
        });

        this.wemapMap.addLayer({
            id: 'polygons',
            type: 'fill',
            source: 'geojson',
            filter: ['==', ['geometry-type'], 'Polygon'],
            paint: {
                'fill-color': ['coalesce', ['get', 'fill'], '#555555'],
                'fill-opacity': ['to-number', ['coalesce', ['get', 'fill-opacity'], 0.5]]
            }
        });

        // Add click handler for features
        map.on('click', (e) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['markers', 'lines', 'polygons']
            });

            if (features.length > 0) {
                const feature = features[0];

                // Remove existing popup if any
                this.clickPopup?.remove();

                // Create popup content
                const popupNode = document.createElement('div');
                const properties = feature.properties || {};

                // Format properties as HTML
                const content = Object.entries(properties)
                    .map(([key, value]) => `<strong>${key}:</strong> ${value}`)
                    .join('<br/>');

                popupNode.innerHTML = content;

                // Create and show popup
                this.clickPopup = new maplibregl.Popup()
                    .setLngLat(e.lngLat)
                    .setDOMContent(popupNode)
                    .addTo(map);
            }
        });

        // Change cursor to pointer when hovering features
        ['markers', 'lines', 'polygons'].forEach(layer => {
            map.on('mouseenter', layer, () => {
                map.getCanvas().style.cursor = 'pointer';
            });

            map.on('mouseleave', layer, () => {
                map.getCanvas().style.cursor = '';
            });
        });

        this.unsubscribeLevelChange = this.wemapMap.onLevelChange(() => this.updateFeatures());
    }

    setHeatmapEnabled(enabled: boolean) {
        this.heatmapEnabled = enabled;
        const map = this.wemapMap.maplibre;
        if (map.getLayer('heatmap')) {
            map.setLayoutProperty('heatmap', 'visibility', enabled ? 'visible' : 'none');
        }
        if (map.getLayer('markers')) {
            map.setLayoutProperty('markers', 'visibility', enabled ? 'none' : 'visible');
        }
    }

    setHeatmapIntensity(intensity: number) {
        this.heatmapIntensity = intensity;
        const map = this.wemapMap.maplibre;
        if (map.getLayer('heatmap')) {
            map.setPaintProperty('heatmap', 'heatmap-intensity', intensity);
        }
    }

    destroy() {
        this.unsubscribeLevelChange?.();
        this.clickPopup?.remove();
        this.wemapMap.removeLayer('heatmap');
        this.wemapMap.removeLayer('markers');
        this.wemapMap.removeLayer('lines');
        this.wemapMap.removeLayer('polygons');
        this.wemapMap.removeSource('geojson');
    }

    updateFeatures() {
        const map = this.wemapMap.maplibre;

        if (!map.style) {
            return;
        }

        const source = map.getSource('geojson') as GeoJSONSource | undefined;

        if (!source) {
            return;
        }

        const mapCurrentLevel = this.wemapMap.getLevel();
        source.setData({
            type: 'FeatureCollection',
            features: this.featuresWithLevel
                .filter(f => f.level === null || Level.intersect(f.level, mapCurrentLevel))
                .map(f => f.feature)
        });
    }
}
